import type { StateStorage } from 'zustand/middleware'
import type { SessionUser } from '@/shared/libs/get-auth-token'
import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'

export type AuthUser = SessionUser

export interface AuthState {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  rememberMe: boolean
  defaultTenantId: string | null
  activeTenantId: string | null
  activeTenantSlug: string | null
  languageCode: string | null
  login: (
    user: AuthUser,
    token: string,
    rememberMe?: boolean,
    context?: {
      defaultTenantId?: string | null
      languageCode?: string | null
      tenantSlug?: string | null
    },
  ) => void
  setActiveTenantId: (tenantId: string | null) => void
  setActiveTenantSlug: (tenantSlug: string | null) => void
  setLanguageCode: (languageCode: string | null) => void
  logout: () => void
  setRememberMe: (rememberMe: boolean) => void
}

const AUTH_STORAGE_KEY = 'auth-storage'
const AUTH_REFRESH_TOKEN_KEY = 'refresh_token'

const authStateStorage: StateStorage = {
  getItem: (name) => {
    const localValue = localStorage.getItem(name)
    if (localValue !== null) {
      return localValue
    }
    return sessionStorage.getItem(name)
  },
  setItem: (name, value) => {
    let rememberMe = true
    try {
      const parsedValue = JSON.parse(value) as { state?: { rememberMe?: boolean } }
      rememberMe = parsedValue.state?.rememberMe ?? true
    } catch {
      rememberMe = true
    }

    const targetStorage = rememberMe ? localStorage : sessionStorage
    const otherStorage = rememberMe ? sessionStorage : localStorage

    targetStorage.setItem(name, value)
    otherStorage.removeItem(name)
  },
  removeItem: (name) => {
    localStorage.removeItem(name)
    sessionStorage.removeItem(name)
  },
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        token: null,
        isAuthenticated: false,
        rememberMe: false,
        defaultTenantId: null,
        activeTenantId: null,
        activeTenantSlug: null,
        languageCode: null,
        login: (user, token, rememberMe, context) =>
          set(
            (state) => {
              const resolvedTenantSlug = context?.tenantSlug ?? user.tenantSlug
              const nextUser =
                resolvedTenantSlug === undefined
                  ? user
                  : { ...user, tenantSlug: resolvedTenantSlug }

              return {
                activeTenantId: context?.defaultTenantId ?? user.tenantId ?? state.activeTenantId,
                activeTenantSlug: context?.tenantSlug ?? user.tenantSlug ?? state.activeTenantSlug,
                user: nextUser,
                token,
                isAuthenticated: true,
                rememberMe: rememberMe ?? state.rememberMe,
                defaultTenantId: context?.defaultTenantId ?? state.defaultTenantId,
                languageCode: context?.languageCode ?? state.languageCode,
              }
            },
            false,
            'auth/login',
          ),
        setActiveTenantId: (tenantId) =>
          set({ activeTenantId: tenantId }, false, 'auth/setActiveTenantId'),
        setActiveTenantSlug: (tenantSlug) =>
          set({ activeTenantSlug: tenantSlug }, false, 'auth/setActiveTenantSlug'),
        setLanguageCode: (languageCode) => set({ languageCode }, false, 'auth/setLanguageCode'),
        logout: () => {
          localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
          sessionStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
          set(
            {
              user: null,
              token: null,
              isAuthenticated: false,
              defaultTenantId: null,
              activeTenantId: null,
              activeTenantSlug: null,
              languageCode: null,
            },
            false,
            'auth/logout',
          )
        },
        setRememberMe: (rememberMe) => set({ rememberMe }, false, 'auth/setRememberMe'),
      }),
      {
        name: AUTH_STORAGE_KEY,
        storage: createJSONStorage(() => authStateStorage),
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          rememberMe: state.rememberMe,
          defaultTenantId: state.defaultTenantId,
          activeTenantId: state.activeTenantId,
          activeTenantSlug: state.activeTenantSlug,
          languageCode: state.languageCode,
        }),
      },
    ),
    { enabled: import.meta.env.DEV, name: 'AuthStore' },
  ),
)
